import { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        subject: '',
        message: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'UK Office',
      details: ['44 Linton Gardens', 'London E6 5SQ', 'United Kingdom'],
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Nigeria Office',
      details: ['Warri', 'Delta State', 'Nigeria'],
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Phone',
      details: ['+44 7915 697011 (UK)', '+234 810 734 9363 (NG)'],
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      details: ['admin@godstonprojects.org', 'epetegp@godstonprojects.org'],
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F5F5F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-[#FF6B00] text-white text-xs uppercase tracking-widest font-bold mb-6">
              Get in Touch
            </span>
            <h1 className="font-['Barlow_Condensed'] text-4xl md:text-6xl font-bold text-[#0A0A0A] uppercase leading-tight tracking-tight mb-6">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Ready to discuss your project? Get in touch with our team today for a consultation or quote.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2 bg-[#0A0A0A] p-8 md:p-12">
              <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-white uppercase mb-8">
                Contact Information
              </h2>
              <div className="space-y-8">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-[#FF6B00] flex items-center justify-center flex-shrink-0">
                      <span className="text-white">{info.icon}</span>
                    </div>
                    <div>
                      <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-2">
                        {info.title}
                      </h4>
                      {info.details.map((detail, dIndex) => (
                        <p key={dIndex} className="text-slate-400 text-sm">
                          {detail}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-slate-800">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-[#FF6B00]" />
                  <div>
                    <p className="text-white font-bold uppercase tracking-wider text-sm">
                      Business Hours
                    </p>
                    <p className="text-slate-400 text-sm">
                      Monday - Friday: 8:00 AM - 5:00 PM
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <h2 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-bold text-[#0A0A0A] uppercase mb-8">
                Send Us a Message
              </h2>

              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-green-50 border border-green-200 flex items-center gap-3" data-testid="form-success">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <p className="text-green-700">Thank you! Your message has been sent successfully. We'll get back to you soon.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 flex items-center gap-3" data-testid="form-error">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                  <p className="text-red-700">Something went wrong. Please try again later.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      className="rounded-none border-slate-300 focus:border-[#FF6B00] focus:ring-[#FF6B00] h-12"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                      className="rounded-none border-slate-300 focus:border-[#FF6B00] focus:ring-[#FF6B00] h-12"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                      Phone Number
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+44 123 456 7890"
                      className="rounded-none border-slate-300 focus:border-[#FF6B00] focus:ring-[#FF6B00] h-12"
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                      Company
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="rounded-none border-slate-300 focus:border-[#FF6B00] focus:ring-[#FF6B00] h-12"
                      data-testid="input-company"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                    Subject *
                  </Label>
                  <Input
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    placeholder="How can we help?"
                    className="rounded-none border-slate-300 focus:border-[#FF6B00] focus:ring-[#FF6B00] h-12"
                    data-testid="input-subject"
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    placeholder="Tell us about your project..."
                    rows={6}
                    className="rounded-none border-slate-300 focus:border-[#FF6B00] focus:ring-[#FF6B00] resize-none"
                    data-testid="input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-[#FF6B00] text-white hover:bg-[#E65000] rounded-none uppercase tracking-wider font-bold px-10 py-6 text-base btn-glow disabled:opacity-50"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>Sending...</>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 h-5 w-5" />
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[400px] bg-slate-200" data-testid="map-section">
        <iframe
          title="Godston Projects UK Office Location"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2481.8399445082783!2d0.04747231574666!3d51.52339397963876!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a7d6d2b0a5c5%3A0x8d6d7f3f3f3f3f3f!2sLinton%20Gardens%2C%20London!5e0!3m2!1sen!2suk!4v1620000000000!5m2!1sen!2suk"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </section>

      {/* Quick Contact Bar */}
      <section className="py-12 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase">
                Need Immediate Assistance?
              </h3>
              <p className="text-slate-400 mt-1">Our team is available 24/7 for emergencies</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="tel:+447915697011"
                className="inline-flex items-center justify-center gap-2 bg-[#FF6B00] text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-[#E65000] transition-colors"
                data-testid="emergency-call-uk"
              >
                <Phone size={18} />
                Call UK Office
              </a>
              <a 
                href="tel:+2348107349363"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 font-bold uppercase tracking-wider hover:bg-white hover:text-[#0A0A0A] transition-colors"
                data-testid="emergency-call-ng"
              >
                <Phone size={18} />
                Call Nigeria Office
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
